/**
 * MAIL PACKETS PAGE
 * 
 * Purpose: Mail dispute packets to bureaus
 * - Shows ALL generated documents from wizard in a reorderable list
 * - Drag & drop to reorder documents within each bureau
 * - Preview shows EXACT order that will be mailed
 * - Download creates same exact file as preview
 * - CONFIRMATION REQUIRED before sending mail
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail, Upload, FileText, X, Send, Truck, BookOpen, Users, CheckCircle2, Loader2, Package, Eye,
  GripVertical, ArrowUp, ArrowDown, Download, AlertTriangle, ChevronDown, ChevronUp,
  ExternalLink, Copy, Check, DollarSign
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const BUREAUS = [
  { 
    key: 'experian', 
    name: 'Experian', 
    color: 'bg-blue-600',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400',
    address: 'P.O. Box 4500, Allen, TX 75013'
  },
  { 
    key: 'equifax', 
    name: 'Equifax', 
    color: 'bg-red-600',
    borderColor: 'border-red-500',
    textColor: 'text-red-400',
    address: 'P.O. Box 740256, Atlanta, GA 30374-0256'
  },
  { 
    key: 'transunion', 
    name: 'TransUnion', 
    color: 'bg-green-600',
    borderColor: 'border-green-500',
    textColor: 'text-green-400',
    address: 'P.O. Box 2000, Chester, PA 19016-2000'
  }
];

// Document type display names
const DOC_TYPE_NAMES = {
  COVER_PAGE: 'Cover Letter',
  DECLARATION_PAGE: 'Handwritten Declaration',
  ACCOUNT_DISPUTE_LETTER: 'Account Dispute Letter',
  INQUIRY_REMOVAL_LETTER: 'Inquiry Removal Letter',
  FTC_REPORT: 'FTC Identity Theft Report',
  LATE_PAYMENT_LETTER: 'Late Payment Dispute Letter',
  BANKRUPTCY_REMOVAL_LETTER: 'Bankruptcy Removal Letter',
  ID_DOC: 'Government ID Copy',
  SOCIAL_SECURITY_CARD: 'Social Security Card Copy'
};

// Default document order for mailing (matches Lob.com requirements)
const DEFAULT_DOC_ORDER = [
  'COVER_PAGE',
  'DECLARATION_PAGE',
  'ACCOUNT_DISPUTE_LETTER',
  'INQUIRY_REMOVAL_LETTER',
  'LATE_PAYMENT_LETTER',
  'FTC_REPORT',
  'BANKRUPTCY_REMOVAL_LETTER',
  'ID_DOC',
  'SOCIAL_SECURITY_CARD'
];

export default function MailPacketsPage({ 
  clientId, 
  clientData,
  bureauFolders = { EQUIFAX: [], EXPERIAN: [], TRANSUNION: [] },
  onReorderDocuments,
  onRemoveDocument
}) {
  // LOB STATUS - Green/Red indicator for mailing system
  const [lobStatus, setLobStatus] = useState({
    checking: true,
    connected: false,
    keyType: null,
    balance: null,
    error: null
  });
  
  // Check Lob status on component mount
  useEffect(() => {
    const checkLobStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/mail/check-lob-status`);
        const data = response.data;
        setLobStatus({
          checking: false,
          connected: data.account_valid || data.configured,
          keyType: data.key_type,
          balance: data.account_info?.balance,
          error: data.error
        });
      } catch (err) {
        setLobStatus({
          checking: false,
          connected: false,
          keyType: null,
          balance: null,
          error: 'Unable to check mailing status'
        });
      }
    };
    checkLobStatus();
  }, []);
  
  // Document order state for each bureau (maintains user's custom order)
  const [documentOrder, setDocumentOrder] = useState({
    EXPERIAN: [],
    EQUIFAX: [],
    TRANSUNION: []
  });
  
  // Expanded/collapsed state for document lists
  const [expandedBureaus, setExpandedBureaus] = useState({
    EXPERIAN: true,
    EQUIFAX: true,
    TRANSUNION: true
  });
  
  // Experian upload modal state
  const [showExperianUploadModal, setShowExperianUploadModal] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Initialize document order from bureauFolders when they change
  useEffect(() => {
    const initOrder = {};
    ['EXPERIAN', 'EQUIFAX', 'TRANSUNION'].forEach(bureau => {
      const docs = bureauFolders[bureau] || [];
      if (docs.length > 0) {
        // Sort by default order first, then maintain existing order
        const sorted = [...docs].sort((a, b) => {
          const orderA = DEFAULT_DOC_ORDER.indexOf(a.docType);
          const orderB = DEFAULT_DOC_ORDER.indexOf(b.docType);
          if (orderA === -1 && orderB === -1) return 0;
          if (orderA === -1) return 1;
          if (orderB === -1) return -1;
          return orderA - orderB;
        });
        initOrder[bureau] = sorted;
      } else {
        initOrder[bureau] = [];
      }
    });
    setDocumentOrder(initOrder);
  }, [bureauFolders]);

  // Legacy file states for drag-drop upload (kept for backward compatibility)
  const [files, setFiles] = useState({
    experian: [],
    equifax: [],
    transunion: []
  });

  const [dragOver, setDragOver] = useState({
    experian: false,
    equifax: false,
    transunion: false
  });

  // Mailing states
  const [sending, setSending] = useState({
    experian: false,
    equifax: false,
    transunion: false
  });

  const [mailResults, setMailResults] = useState({
    experian: null,
    equifax: null,
    transunion: null
  });

  // Per-bureau mail type selection - Default to First Class
  const [bureauMailTypes, setBureauMailTypes] = useState({
    experian: 'usps_first_class',
    equifax: 'usps_first_class',
    transunion: 'usps_first_class'
  });
  
  // Sending all bureaus state
  const [sendingAll, setSendingAll] = useState(false);

  const mailTypeOptions = [
    { value: 'usps_first_class', label: 'First Class', cost: '$1.50' },
    { value: 'certified', label: 'Certified Mail', cost: '$8.50' },
    { value: 'certified_return_receipt', label: 'Certified + Return Receipt', cost: '$12.50' }
  ];

  // ═══════════════════════════════════════════════════════════════════
  // EXPERIAN UPLOAD HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════
  
  // Copy text to clipboard
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  // Open Experian upload page and show helper modal
  const openExperianUpload = () => {
    setShowExperianUploadModal(true);
    window.open('https://www.experian.com/consumer/upload/', '_blank');
  };
  
  // Parse name into first and last name from clientData
  const getNameParts = () => {
    const fullName = clientData?.name || '';
    const nameParts = fullName.trim().split(/\s+/);
    return {
      firstName: nameParts[0] || '',
      middleInitial: nameParts.length > 2 ? nameParts[1]?.charAt(0) || '' : '',
      lastName: nameParts.length > 2 ? nameParts.slice(2).join(' ') : nameParts[1] || ''
    };
  };

  // ═══════════════════════════════════════════════════════════════════
  // DOCUMENT REORDERING FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════
  
  // Move a document up in the list
  const moveDocumentUp = (bureau, index) => {
    if (index <= 0) return;
    
    setDocumentOrder(prev => {
      const docs = [...(prev[bureau] || [])];
      [docs[index - 1], docs[index]] = [docs[index], docs[index - 1]];
      return { ...prev, [bureau]: docs };
    });
  };
  
  // Move a document down in the list
  const moveDocumentDown = (bureau, index) => {
    setDocumentOrder(prev => {
      const docs = [...(prev[bureau] || [])];
      if (index >= docs.length - 1) return prev;
      [docs[index], docs[index + 1]] = [docs[index + 1], docs[index]];
      return { ...prev, [bureau]: docs };
    });
  };
  
  // Remove a document from a bureau
  const handleRemoveDoc = (bureau, docId) => {
    setDocumentOrder(prev => ({
      ...prev,
      [bureau]: (prev[bureau] || []).filter(d => d.id !== docId && d.docType !== docId)
    }));
    
    // Call parent handler if provided
    if (onRemoveDocument) {
      onRemoveDocument(bureau, docId);
    }
    
    toast.info(`Removed document from ${bureau} package`);
  };
  
  // Drag and drop reordering
  const [draggedDoc, setDraggedDoc] = useState(null);
  const [draggedBureau, setDraggedBureau] = useState(null);
  
  const handleDocDragStart = (bureau, doc, index) => (e) => {
    setDraggedDoc({ doc, index });
    setDraggedBureau(bureau);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDocDragOver = (bureau, index) => (e) => {
    e.preventDefault();
    if (draggedBureau !== bureau) return;
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDocDrop = (bureau, dropIndex) => (e) => {
    e.preventDefault();
    if (!draggedDoc || draggedBureau !== bureau) return;
    
    const { index: dragIndex } = draggedDoc;
    if (dragIndex === dropIndex) return;
    
    setDocumentOrder(prev => {
      const docs = [...(prev[bureau] || [])];
      const [removed] = docs.splice(dragIndex, 1);
      docs.splice(dropIndex, 0, removed);
      return { ...prev, [bureau]: docs };
    });
    
    setDraggedDoc(null);
    setDraggedBureau(null);
  };
  
  const handleDocDragEnd = () => {
    setDraggedDoc(null);
    setDraggedBureau(null);
  };
  
  // Toggle bureau expansion
  const toggleBureauExpand = (bureau) => {
    setExpandedBureaus(prev => ({
      ...prev,
      [bureau]: !prev[bureau]
    }));
  };
  
  // Preview a single document
  const previewDocument = (doc) => {
    if (doc.blob) {
      const url = URL.createObjectURL(doc.blob);
      window.open(url, '_blank');
    } else {
      toast.error('Document preview not available');
    }
  };

  // Handle file drop
  const handleDrop = useCallback((bureau) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragOver(prev => ({ ...prev, [bureau]: false }));
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error('Please drop PDF files only');
      return;
    }
    
    setFiles(prev => ({
      ...prev,
      [bureau]: [...prev[bureau], ...pdfFiles]
    }));
    
    toast.success(`${pdfFiles.length} file(s) added to ${bureau}`);
  }, []);

  // Handle file input
  const handleFileInput = (bureau) => (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const pdfFiles = selectedFiles.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length > 0) {
      setFiles(prev => ({
        ...prev,
        [bureau]: [...prev[bureau], ...pdfFiles]
      }));
      toast.success(`${pdfFiles.length} file(s) added to ${bureau}`);
    }
    
    e.target.value = '';
  };

  // Remove file
  const removeFile = (bureau, index) => {
    setFiles(prev => ({
      ...prev,
      [bureau]: prev[bureau].filter((_, i) => i !== index)
    }));
  };

  // Mail confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingMailBureau, setPendingMailBureau] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [fetchingEstimate, setFetchingEstimate] = useState(false);
  
  // Get cost estimate before sending
  const getCostEstimate = async (bureauKey) => {
    const selectedMailType = bureauMailTypes[bureauKey] || 'certified';
    setFetchingEstimate(true);
    
    try {
      // Call WITHOUT confirmed=true to get estimate only
      const response = await axios.post(
        `${API}/api/mail/send-package/${clientId}/${bureauKey}?mail_type=${selectedMailType}&confirmed=false`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error getting cost estimate:', error);
      throw error;
    } finally {
      setFetchingEstimate(false);
    }
  };
  
  // Show confirmation dialog before sending mail
  const handleSendMailClick = async (bureauKey) => {
    const bureauFiles = files[bureauKey];
    if (bureauFiles.length === 0) {
      toast.error('No files to send');
      return;
    }
    
    try {
      setFetchingEstimate(true);
      const estimate = await getCostEstimate(bureauKey);
      setCostEstimate(estimate);
      setPendingMailBureau(bureauKey);
      setConfirmDialogOpen(true);
    } catch (error) {
      toast.error(`Failed to get cost estimate: ${error.response?.data?.detail || error.message}`);
    } finally {
      setFetchingEstimate(false);
    }
  };
  
  // Confirmed - actually send the mail
  const confirmAndSendMail = async () => {
    if (!pendingMailBureau) return;
    
    const bureauKey = pendingMailBureau;
    setConfirmDialogOpen(false);
    setCostEstimate(null);
    setPendingMailBureau(null);
    
    await sendMailViaLob(bureauKey, true);  // Pass confirmed=true
  };

  // Send mail via Lob API (with explicit confirmation)
  const sendMailViaLob = async (bureauKey, confirmed = false) => {
    const bureauFiles = files[bureauKey];
    if (bureauFiles.length === 0) {
      toast.error('No files to send');
      return;
    }
    
    // SAFETY CHECK: Require explicit confirmation
    if (!confirmed) {
      handleSendMailClick(bureauKey);
      return;
    }

    const selectedMailType = bureauMailTypes[bureauKey] || 'certified';
    setSending(prev => ({ ...prev, [bureauKey]: true }));

    try {
      // Use the send-package endpoint WITH confirmed=true
      const response = await axios.post(
        `${API}/api/mail/send-package/${clientId}/${bureauKey}?mail_type=${selectedMailType}&confirmed=true`
      );

      // Store results
      setMailResults(prev => ({
        ...prev,
        [bureauKey]: {
          success: true,
          data: response.data,
          sentAt: new Date().toISOString()
        }
      }));

      const actualCost = response.data.actual_cost || response.data.estimated_cost;
      if (response.data.lob_id) {
        toast.success(`✅ Mail sent to ${bureauKey.toUpperCase()}! Cost: $${actualCost?.toFixed(2) || 'N/A'} | Tracking: ${response.data.tracking_number || 'Processing'}`);
      } else if (response.data.success) {
        toast.success(`✅ Mail sent to ${bureauKey.toUpperCase()}! Estimated cost: $${actualCost?.toFixed(2) || 'N/A'}`);
      } else {
        toast.info(`Mail queued for ${bureauKey.toUpperCase()}`);
      }

    } catch (error) {
      console.error('Error sending mail:', error);
      setMailResults(prev => ({
        ...prev,
        [bureauKey]: {
          success: false,
          error: error.response?.data?.detail || error.message
        }
      }));
      toast.error(`Failed to send mail to ${bureauKey}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSending(prev => ({ ...prev, [bureauKey]: false }));
    }
  };

  // State for batch mail confirmation
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchEstimates, setBatchEstimates] = useState([]);
  const [fetchingBatchEstimates, setFetchingBatchEstimates] = useState(false);

  // Send ALL bureaus at once - requires confirmation
  const sendAllBureaus = async () => {
    const bureausWithFiles = BUREAUS.filter(b => files[b.key].length > 0 && !mailResults[b.key]?.success);
    
    if (bureausWithFiles.length === 0) {
      toast.error('No packets ready to send');
      return;
    }
    
    // Get estimates for all bureaus first
    setFetchingBatchEstimates(true);
    try {
      const estimates = [];
      for (const bureau of bureausWithFiles) {
        const estimate = await getCostEstimate(bureau.key);
        estimates.push({ ...estimate, bureauKey: bureau.key });
      }
      setBatchEstimates(estimates);
      setBatchConfirmOpen(true);
    } catch (error) {
      toast.error('Failed to get cost estimates');
    } finally {
      setFetchingBatchEstimates(false);
    }
  };
  
  // Confirm and send all bureaus
  const confirmAndSendAllBureaus = async () => {
    setBatchConfirmOpen(false);
    setSendingAll(true);
    
    let successCount = 0;
    let failCount = 0;
    let totalCost = 0;
    
    for (const estimate of batchEstimates) {
      try {
        await sendMailViaLob(estimate.bureauKey, true);  // confirmed=true
        successCount++;
        totalCost += estimate.estimated_cost || 0;
      } catch (err) {
        failCount++;
      }
    }
    
    setSendingAll(false);
    setBatchEstimates([]);
    
    if (successCount > 0 && failCount === 0) {
      toast.success(`🎉 Successfully mailed ${successCount} packet(s)! Total cost: ~$${totalCost.toFixed(2)}`);
    } else if (successCount > 0) {
      toast.warning(`Mailed ${successCount} packet(s), ${failCount} failed. Cost: ~$${totalCost.toFixed(2)}`);
    }
  };

  // Get total cost estimate
  const getTotalCostEstimate = () => {
    const priceMap = {
      'usps_first_class': 1.50,
      'certified': 8.50,
      'certified_return_receipt': 12.50
    };
    
    let total = 0;
    BUREAUS.forEach(bureau => {
      if (files[bureau.key].length > 0 && !mailResults[bureau.key]?.success) {
        total += priceMap[bureauMailTypes[bureau.key]] || 8.50;
      }
    });
    return total.toFixed(2);
  };

  // Get ready packets count
  const getReadyPacketsCount = () => {
    return BUREAUS.filter(b => files[b.key].length > 0 && !mailResults[b.key]?.success).length;
  };

  // Drag handlers
  const handleDragOver = (bureau) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [bureau]: true }));
  };

  const handleDragLeave = (bureau) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [bureau]: false }));
  };

  // Send Credit Repair Steps email via backend API
  const [sendingCreditSteps, setSendingCreditSteps] = useState(false);
  const [sendingPortalAccess, setSendingPortalAccess] = useState(false);
  
  const sendCreditRepairSteps = async () => {
    const email = clientData?.email;
    if (!email) {
      toast.error('Client email not found');
      return;
    }
    
    setSendingCreditSteps(true);
    try {
      const response = await axios.post(`${API}/api/email/send-credit-repair-steps`, {
        client_id: clientId
      });
      
      if (response.data.success) {
        toast.success(`✅ Credit Repair Steps email sent to ${email}!`);
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending credit repair steps:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingCreditSteps(false);
    }
  };

  // Send Client Portal Access email via backend API
  const sendClientPortalAccess = async () => {
    const email = clientData?.email;
    if (!email) {
      toast.error('Client email not found');
      return;
    }
    
    setSendingPortalAccess(true);
    try {
      const response = await axios.post(`${API}/api/email/send-portal-link`, {
        client_id: clientId
      });
      
      if (response.data.success) {
        toast.success(`✅ Portal Access email sent to ${email}! Portal: ${response.data.portal_url}`);
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending portal access:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingPortalAccess(false);
    }
  };

  // Count total files (both from bureauFolders and legacy file uploads)
  const totalBureauDocs = Object.values(documentOrder).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const totalFiles = Object.values(files).reduce((sum, arr) => sum + arr.length, 0) + totalBureauDocs;

  // Preview state
  const [previewLoading, setPreviewLoading] = useState({
    experian: false,
    equifax: false,
    transunion: false
  });
  
  // Download state
  const [downloadLoading, setDownloadLoading] = useState({
    EXPERIAN: false,
    EQUIFAX: false,
    TRANSUNION: false
  });

  // ═══════════════════════════════════════════════════════════════════
  // PREVIEW MAIL PACKAGE - Uses the EXACT document order from the list
  // This merges all documents in the current order into a single PDF
  // Shows labeled blank page so user can SEE it
  // ═══════════════════════════════════════════════════════════════════
  const previewMailPackage = async (bureauKey) => {
    const upperBureau = bureauKey.toUpperCase();
    const docs = documentOrder[upperBureau] || [];
    
    if (docs.length === 0) {
      toast.error(`No documents found for ${bureauKey}. Generate documents in previous wizard steps first.`);
      return;
    }
    
    setPreviewLoading(prev => ({ ...prev, [bureauKey]: true }));
    
    try {
      // Use pdf-lib to merge all PDFs in the exact order shown
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();
      
      // Add LABELED blank first page (so user can SEE it in preview)
      const blankPage = mergedPdf.addPage();
      const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
      blankPage.drawText('[ BLANK PAGE FOR LOB.COM ADDRESS ]', {
        x: 150,
        y: 400,
        size: 14,
        font: font,
        color: rgb(0.6, 0.6, 0.6),
      });
      blankPage.drawText('Lob.com prints the mailing address on this page.', {
        x: 160,
        y: 380,
        size: 10,
        font: font,
        color: rgb(0.6, 0.6, 0.6),
      });
      blankPage.drawText('Your documents start on the next page.', {
        x: 175,
        y: 360,
        size: 10,
        font: font,
        color: rgb(0.6, 0.6, 0.6),
      });
      
      // Add each document in order
      for (const doc of docs) {
        if (doc.blob) {
          try {
            const docBytes = await doc.blob.arrayBuffer();
            const docPdf = await PDFDocument.load(docBytes);
            const pages = await mergedPdf.copyPages(docPdf, docPdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch (err) {
            console.error(`Error adding ${doc.docType}:`, err);
          }
        }
      }
      
      // Generate and open the merged PDF
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      toast.success(`Preview opened for ${bureauKey.toUpperCase()} - ${docs.length} documents (+ 1 blank page for Lob address)`);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview - ensure all documents are valid PDFs');
    } finally {
      setPreviewLoading(prev => ({ ...prev, [bureauKey]: false }));
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // DOWNLOAD MAIL PACKAGE - Creates the EXACT same file as preview
  // This is what gets mailed - must match preview exactly
  // ═══════════════════════════════════════════════════════════════════
  const downloadMailPackage = async (bureau) => {
    const docs = documentOrder[bureau] || [];
    
    if (docs.length === 0) {
      toast.error(`No documents to download for ${bureau}`);
      return;
    }
    
    setDownloadLoading(prev => ({ ...prev, [bureau]: true }));
    
    try {
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();
      
      // Add labeled blank first page
      const blankPage = mergedPdf.addPage();
      const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
      blankPage.drawText('[ BLANK PAGE FOR LOB.COM ADDRESS ]', {
        x: 150,
        y: 400,
        size: 14,
        font: font,
        color: rgb(0.6, 0.6, 0.6),
      });
      
      // Add each document in the EXACT order shown in the list
      for (const doc of docs) {
        if (doc.blob) {
          try {
            const docBytes = await doc.blob.arrayBuffer();
            const docPdf = await PDFDocument.load(docBytes);
            const pages = await mergedPdf.copyPages(docPdf, docPdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch (err) {
            console.error(`Error adding ${doc.docType}:`, err);
          }
        }
      }
      
      // Download the merged PDF
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bureau}_Dispute_Package_${clientData?.name?.replace(/\s+/g, '_') || 'Client'}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${bureau} package with ${docs.length} documents!`);
    } catch (error) {
      console.error('Error downloading package:', error);
      toast.error('Failed to download package');
    } finally {
      setDownloadLoading(prev => ({ ...prev, [bureau]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* LOB MAILING STATUS INDICATOR - Green = Active, Red = Inactive */}
      <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${
        lobStatus.checking 
          ? 'bg-zinc-800 border-zinc-600' 
          : lobStatus.connected 
            ? 'bg-green-900/30 border-green-500' 
            : 'bg-red-900/30 border-red-500'
      }`}>
        <div className="flex items-center gap-3">
          {/* Status Light */}
          <div className={`w-4 h-4 rounded-full ${
            lobStatus.checking 
              ? 'bg-yellow-500 animate-pulse' 
              : lobStatus.connected 
                ? 'bg-green-500 shadow-lg shadow-green-500/50' 
                : 'bg-red-500 shadow-lg shadow-red-500/50'
          }`} />
          
          {/* Status Text */}
          <div>
            <div className={`font-semibold ${
              lobStatus.checking 
                ? 'text-yellow-400' 
                : lobStatus.connected 
                  ? 'text-green-400' 
                  : 'text-red-400'
            }`}>
              {lobStatus.checking 
                ? 'Checking Mailing System...' 
                : lobStatus.connected 
                  ? '● Lob.com Mailing ACTIVE' 
                  : '● Lob.com Mailing INACTIVE'}
            </div>
            <div className="text-xs text-zinc-400">
              {lobStatus.checking 
                ? 'Verifying connection...' 
                : lobStatus.connected 
                  ? `${lobStatus.keyType || 'LIVE'} mode - Ready to send mail` 
                  : lobStatus.error || 'Connection issue - check your API key'}
            </div>
          </div>
        </div>
        
        {/* Balance Display (if available) */}
        {lobStatus.connected && lobStatus.balance !== null && (
          <div className="text-right">
            <div className="text-sm text-zinc-400">Account Balance</div>
            <div className="text-lg font-bold text-green-400">
              ${(lobStatus.balance / 100).toFixed(2)}
            </div>
          </div>
        )}
        
        {/* Error Action Button */}
        {!lobStatus.checking && !lobStatus.connected && (
          <Button 
            variant="outline" 
            size="sm"
            className="border-red-500 text-red-400 hover:bg-red-500/20"
            onClick={() => window.open('https://dashboard.lob.com/settings/api-keys', '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Fix in Lob Dashboard
          </Button>
        )}
      </div>
      
      {/* Header */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Mail className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-white">Mail Dispute Packets</CardTitle>
              <p className="text-sm text-zinc-400">
                Upload PDFs and send to credit bureaus via Lob.com
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SIMPLIFIED: Go directly to manual file upload section */}
      {/* The old document list sections that showed "No Documents Generated" are removed */}
      {/* ═══════════════════════════════════════════════════════════════════ */}

      {/* Hidden state update for backwards compatibility - NOT RENDERED */}
      {false && BUREAUS.map((bureau) => {
          const upperBureau = bureau.key.toUpperCase();
          const docs = documentOrder[upperBureau] || [];
          const isExpanded = expandedBureaus[upperBureau];
          const hasDocuments = docs.length > 0;
          
          return (
            <Card 
              key={bureau.key}
              className={`border-2 transition-all ${
                hasDocuments 
                  ? `${bureau.borderColor} bg-zinc-900/80` 
                  : 'border-zinc-700 bg-zinc-900/50'
              }`}
              data-testid={`mail-package-${bureau.key}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${bureau.color} text-white font-bold px-3 py-1`}>
                      {bureau.name}
                    </Badge>
                    <Badge variant="outline" className={hasDocuments ? 'text-green-400 border-green-500' : 'text-zinc-500 border-zinc-600'}>
                      {docs.length} document{docs.length !== 1 ? 's' : ''}
                    </Badge>
                    <span className="text-xs text-zinc-500">{bureau.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasDocuments && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => previewMailPackage(bureau.key)}
                          disabled={previewLoading[bureau.key]}
                          className={`${bureau.borderColor} border hover:bg-zinc-800`}
                          data-testid={`preview-${bureau.key}-btn`}
                        >
                          {previewLoading[bureau.key] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadMailPackage(upperBureau)}
                          disabled={downloadLoading[upperBureau]}
                          className="border-green-500 text-green-400 hover:bg-green-500/10"
                          data-testid={`download-${bureau.key}-btn`}
                        >
                          {downloadLoading[upperBureau] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </>
                          )}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleBureauExpand(upperBureau)}
                      className="text-zinc-400"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-2">
                  {hasDocuments ? (
                    <>
                      {/* Document List Header */}
                      <div className="flex items-center justify-between mb-3 px-2">
                        <span className="text-xs text-zinc-400 uppercase tracking-wide">Document Order (drag to reorder)</span>
                        <span className="text-xs text-zinc-500">Page 1 is blank for Lob.com address</span>
                      </div>
                      
                      {/* Reorderable Document List */}
                      <div className="space-y-2">
                        {docs.map((doc, index) => (
                          <div
                            key={doc.id || `${doc.docType}-${index}`}
                            draggable
                            onDragStart={handleDocDragStart(upperBureau, doc, index)}
                            onDragOver={handleDocDragOver(upperBureau, index)}
                            onDrop={handleDocDrop(upperBureau, index)}
                            onDragEnd={handleDocDragEnd}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                              draggedDoc?.doc?.id === doc.id && draggedBureau === upperBureau
                                ? 'border-yellow-500 bg-yellow-500/10 opacity-50'
                                : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-800'
                            }`}
                            data-testid={`doc-item-${bureau.key}-${index}`}
                          >
                            {/* Drag Handle */}
                            <GripVertical className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            
                            {/* Order Number */}
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${bureau.color}`}>
                              {index + 2}
                            </div>
                            
                            {/* Document Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">
                                {DOC_TYPE_NAMES[doc.docType] || doc.fileName || doc.docType}
                              </p>
                              {doc.fileName && doc.fileName !== DOC_TYPE_NAMES[doc.docType] && (
                                <p className="text-xs text-zinc-500 truncate">{doc.fileName}</p>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveDocumentUp(upperBureau, index)}
                                disabled={index === 0}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                                title="Move up"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveDocumentDown(upperBureau, index)}
                                disabled={index === docs.length - 1}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                                title="Move down"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => previewDocument(doc)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-cyan-400"
                                title="Preview document"
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveDoc(upperBureau, doc.id || doc.docType)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
                                title="Remove from package"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Package Info */}
                      <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <Package className="w-4 h-4 text-green-400" />
                          <span className="text-zinc-300">
                            Final package: <span className="text-white font-semibold">{docs.length + 1} pages</span> (1 blank + {docs.length} documents)
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Preview and Download will create identical PDFs with documents in the exact order shown above
                        </p>
                      </div>
                      
                      {/* Experian Upload Online Option - Only for Experian */}
                      {bureau.key === 'experian' && (
                        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="text-blue-300 font-medium text-sm">Upload directly to Experian</p>
                              <p className="text-xs text-zinc-400">Upload documents online instead of mailing</p>
                            </div>
                            <Button
                              onClick={openExperianUpload}
                              className="bg-blue-600 hover:bg-blue-700 font-semibold"
                              data-testid="wizard-experian-upload-btn"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Upload to Experian
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="py-8 text-center">
                      <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                      <p className="text-zinc-400 mb-2">No documents generated for {bureau.name}</p>
                      <p className="text-xs text-zinc-500">
                        Go back to previous wizard steps to generate dispute letters, FTC report, and upload ID/SSN
                      </p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DROP ZONES - For file uploads to send via Lob.com */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Drop PDF Files to Mail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-400 mb-4">
            Drop your PDF files here to send to credit bureaus via Lob.com
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {BUREAUS.map((bureau) => {
              const bureauFiles = files[bureau.key];
              const isDragOver = dragOver[bureau.key];
              
              return (
                <div
                  key={bureau.key}
                  onDrop={handleDrop(bureau.key)}
                  onDragOver={handleDragOver(bureau.key)}
                  onDragLeave={handleDragLeave(bureau.key)}
                  className={`p-4 rounded-lg border-2 border-dashed transition-all ${
                    isDragOver 
                      ? `${bureau.borderColor} bg-zinc-800/50` 
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className={`${bureau.textColor} border-current text-xs`}>
                      {bureau.name}
                    </Badge>
                    {bureauFiles.length > 0 && (
                      <span className="text-xs text-zinc-400">{bureauFiles.length} file(s)</span>
                    )}
                  </div>
                  
                  {bureauFiles.length === 0 ? (
                    <div className="text-center py-4">
                      <Upload className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
                      <p className="text-xs text-zinc-500">Drop PDF files</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {bureauFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-xs bg-zinc-800 rounded px-2 py-1">
                          <span className="truncate text-zinc-300">{file.name}</span>
                          <button 
                            onClick={() => removeFile(bureau.key, index)}
                            className="text-red-400 hover:text-red-300 ml-2"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions Bar - Send All */}
      {totalFiles > 0 && (
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-400" />
                  <span className="text-green-200 font-semibold">
                    {getReadyPacketsCount()} Packet(s) Ready
                  </span>
                </div>
                <Badge variant="outline" className="text-green-400 border-green-400">
                  Est. ${getTotalCostEstimate()}
                </Badge>
              </div>
              
              <Button
                onClick={sendAllBureaus}
                disabled={sendingAll || getReadyPacketsCount() === 0}
                className="bg-green-600 hover:bg-green-700 font-bold px-6"
                data-testid="send-all-bureaus-btn"
              >
                {sendingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending All...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send All via Lob
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Bureau Send Cards with Mail Type Selection */}
      {totalFiles > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {BUREAUS.map((bureau) => {
            const bureauFiles = files[bureau.key];
            const result = mailResults[bureau.key];
            const isSending = sending[bureau.key];
            const mailTypeValue = bureauMailTypes[bureau.key];
            
            if (bureauFiles.length === 0) return null;
            
            return (
              <Card key={`send-${bureau.key}`} className={`border-2 ${bureau.borderColor} bg-zinc-900/50`}>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className={`${bureau.color} text-white font-bold`}>
                      {bureau.name}
                    </Badge>
                    <span className="text-zinc-400 text-sm">{bureauFiles.length} file(s)</span>
                  </div>
                  
                  {/* Mail Type Selector per bureau */}
                  {!result?.success && (
                    <div className="space-y-2">
                      <label className="text-xs text-zinc-400 flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Mail Type:
                      </label>
                      <select
                        value={mailTypeValue}
                        onChange={(e) => setBureauMailTypes(prev => ({ ...prev, [bureau.key]: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-600 text-white rounded-lg px-3 py-2 text-sm"
                        data-testid={`mail-type-${bureau.key}`}
                      >
                        {mailTypeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} ({opt.cost})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {result?.success ? (
                    <div className="text-center py-3 bg-green-500/10 rounded-lg">
                      <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
                      <p className="text-green-300 font-semibold">Mail Sent!</p>
                      {result.data?.tracking_number && (
                        <p className="text-zinc-400 text-xs mt-1 font-mono">
                          Tracking: {result.data.tracking_number}
                        </p>
                      )}
                      {result.data?.expected_delivery && (
                        <p className="text-zinc-400 text-xs">
                          Expected: {result.data.expected_delivery}
                        </p>
                      )}
                    </div>
                  ) : result?.error ? (
                    <div className="text-center py-3 bg-red-500/10 rounded-lg">
                      <X className="w-10 h-10 text-red-400 mx-auto mb-2" />
                      <p className="text-red-300 font-semibold">Failed to send</p>
                      <p className="text-zinc-500 text-xs mt-1">{result.error}</p>
                      <Button
                        onClick={() => setMailResults(prev => ({ ...prev, [bureau.key]: null }))}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => sendMailViaLob(bureau.key)}
                      disabled={isSending || sendingAll}
                      className={`w-full ${bureau.color} hover:opacity-90 font-bold`}
                      data-testid={`send-lob-${bureau.key}`}
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending via Lob...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Mail to {bureau.name}
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mailing Instructions */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Truck className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <h4 className="text-white font-medium">Mailing Instructions</h4>
              <ul className="text-sm text-zinc-400 mt-2 space-y-1">
                <li>• Send each bureau&apos;s packet via certified mail with return receipt</li>
                <li>• Keep copies of all documents for your records</li>
                <li>• Allow 30-45 days for bureau response</li>
                <li>• Track your certified mail using USPS tracking</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Buttons - Side by Side */}
      <div className="flex justify-center gap-4 pt-4">
        <Button
          onClick={sendCreditRepairSteps}
          className="bg-indigo-600 hover:bg-indigo-700 px-8"
          data-testid="send-credit-repair-steps-btn"
          disabled={sendingCreditSteps}
        >
          {sendingCreditSteps ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <BookOpen className="w-4 h-4 mr-2" />
          )}
          {sendingCreditSteps ? 'Sending...' : 'Send Credit Repair Steps'}
        </Button>
        <Button
          onClick={sendClientPortalAccess}
          className="bg-purple-600 hover:bg-purple-700 px-8"
          data-testid="send-client-portal-access-btn"
          disabled={sendingPortalAccess}
        >
          {sendingPortalAccess ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Users className="w-4 h-4 mr-2" />
          )}
          {sendingPortalAccess ? 'Sending...' : 'Send Client Portal Access'}
        </Button>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EXPERIAN UPLOAD HELPER MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showExperianUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-blue-500 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <ExternalLink className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">Experian Upload Helper</CardTitle>
                    <p className="text-sm text-zinc-400">Copy client info to paste into Experian form</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowExperianUploadModal(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">
                The Experian upload page has opened in a new tab. Use the copy buttons below to paste client info into the form.
              </p>
              
              {/* First Name */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">First Name</p>
                  <p className="text-white font-mono">{getNameParts().firstName || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(getNameParts().firstName, 'First Name')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'First Name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Last Name */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">Last Name</p>
                  <p className="text-white font-mono">{getNameParts().lastName || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(getNameParts().lastName, 'Last Name')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'Last Name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Address */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">Address</p>
                  <p className="text-white font-mono">{clientData?.address || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.address || '', 'Address')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'Address' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* City */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">City</p>
                  <p className="text-white font-mono">{clientData?.city || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.city || '', 'City')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'City' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* State */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">State</p>
                  <p className="text-white font-mono">{clientData?.state || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.state || '', 'State')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'State' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* ZIP Code */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">ZIP Code</p>
                  <p className="text-white font-mono">{clientData?.zip || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.zip || '', 'ZIP Code')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'ZIP Code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Social Security Number */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-red-500/30">
                <div className="flex-1">
                  <p className="text-xs text-red-400">Social Security Number / ITIN</p>
                  <p className="text-white font-mono">{clientData?.ssn || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.ssn || '', 'SSN')}
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  {copiedField === 'SSN' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Email */}
              <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-xs text-zinc-400">Email Address</p>
                  <p className="text-white font-mono">{clientData?.email || '-'}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(clientData?.email || '', 'Email')}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                >
                  {copiedField === 'Email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="pt-3 border-t border-zinc-700">
                <Button
                  onClick={() => setShowExperianUploadModal(false)}
                  className="w-full bg-zinc-700 hover:bg-zinc-600"
                >
                  Done
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* MAIL CONFIRMATION DIALOG */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Confirm Mail Send
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Please review the cost before sending mail to Lob.com
            </DialogDescription>
          </DialogHeader>
          
          {costEstimate && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-zinc-400">Client:</span>
                  <span className="text-white font-medium">{costEstimate.client_name}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-zinc-400">Bureau:</span>
                  <span className="text-white font-medium">{costEstimate.bureau}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-zinc-400">Mail Type:</span>
                  <span className="text-white font-medium capitalize">{costEstimate.mail_type?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-zinc-400">Pages:</span>
                  <span className="text-white font-medium">{costEstimate.page_count}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-zinc-400">Documents:</span>
                  <span className="text-white font-medium">{costEstimate.document_count}</span>
                </div>
                
                <div className="border-t border-zinc-600 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-yellow-400 font-semibold">Estimated Cost:</span>
                    <span className="text-2xl text-green-400 font-bold">
                      ${costEstimate.estimated_cost?.toFixed(2)}
                    </span>
                  </div>
                  {costEstimate.cost_breakdown && (
                    <div className="mt-2 text-xs text-zinc-500">
                      Base fee: ${costEstimate.cost_breakdown.base_fee} + 
                      ${costEstimate.cost_breakdown.per_page_fee}/page × {costEstimate.cost_breakdown.pages} pages
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  This will charge your Lob.com account and send physical mail.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setConfirmDialogOpen(false);
                setCostEstimate(null);
                setPendingMailBureau(null);
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAndSendMail}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Confirm & Send Mail (${costEstimate?.estimated_cost?.toFixed(2)})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* BATCH MAIL CONFIRMATION DIALOG */}
      <Dialog open={batchConfirmOpen} onOpenChange={setBatchConfirmOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Confirm Batch Mail Send
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Review costs for all bureaus before sending
            </DialogDescription>
          </DialogHeader>
          
          {batchEstimates.length > 0 && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700 max-h-64 overflow-y-auto">
                {batchEstimates.map((estimate, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-zinc-700 last:border-0">
                    <div>
                      <span className="text-white font-medium">{estimate.bureau}</span>
                      <p className="text-xs text-zinc-500">{estimate.page_count} pages, {estimate.document_count} docs</p>
                    </div>
                    <span className="text-green-400 font-bold">${estimate.estimated_cost?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg text-white font-semibold">Total Estimated Cost:</span>
                  <span className="text-2xl text-green-400 font-bold">
                    ${batchEstimates.reduce((sum, e) => sum + (e.estimated_cost || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  This will charge your Lob.com account for {batchEstimates.length} letter(s).
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setBatchConfirmOpen(false);
                setBatchEstimates([]);
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAndSendAllBureaus}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Confirm & Send All (${batchEstimates.reduce((sum, e) => sum + (e.estimated_cost || 0), 0).toFixed(2)})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
